import { Activity, ChevronRight } from 'lucide-react';
import { ErrorBoundary } from 'react-error-boundary';

import { Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Empty from '@/components/Empty/Empty';
import List from '@/components/List/List';
import Typo, { Span } from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import {
  DYNAMIC_FILTER_QUERY_KEY,
  PERIOD_QUERY_KEY,
  PERIOD_VALUE_QUERY_KEY,
} from '@/features/(signal)/signals/hooks';
import { SIGNAL_DATE_PRESET } from '@/features/(signal)/signals/model';
import ErrorDisplay from '@/features/Dashboard/components/ErrorDisplay';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import { track } from '@/utils/track/track';

import { useHomeTopSignals } from '../hooks';
import HomeTopSignalRow from './home-top-signal-row';
const DATE_PRESET = SIGNAL_DATE_PRESET.PAST_7_DAYS;
const EMPTY_FILTER_SET: StructuredFilter = {};

const HomeTopSignals = () => {
  const wr = useWorkspaceRouter();
  const { topIssues, isLoading, period } = useHomeTopSignals({
    datePreset: DATE_PRESET,
  });

  const onRowClick = (issueName: string, severity: string) => {
    track(AMP_KEYS.SIGNAL_ISSUE_DETAIL_VIEW, {
      issue_name: issueName,
      severity,
      view_type: 'row_click',
    });

    const query = new URLSearchParams({
      [DYNAMIC_FILTER_QUERY_KEY]: JSON.stringify(EMPTY_FILTER_SET),
      [PERIOD_QUERY_KEY]: period.unit,
      [PERIOD_VALUE_QUERY_KEY]: period.value.toString(),
    });
    wr.push(`/signals/${encodeURIComponent(issueName)}?${query.toString()}`);
  };

  return (
    <ErrorBoundary fallbackRender={() => <ErrorDisplay />}>
      <VFlex g={10} f={1}>
        <Flex j="space-between" a="center">
          <Flex a="center" g={8}>
            <Typo type="label2" weight="m" className="text-gray-700">
              Top 10 signals
            </Typo>
            <Span type="label3" className="text-gray-600">
              Past 7 days
            </Span>
          </Flex>
          <Button
            variant="ghost_secondary"
            edgeAlign="right"
            rightIcon={<ChevronRight />}
            onClick={() => {
              wr.push('/signals');
            }}
          >
            View all
          </Button>
        </Flex>

        <List
          className="max-h-[626px] h-full overflow-y-auto border border-gray-300 rounded-2xl p-2"
          data={topIssues}
          isLoading={isLoading}
          placeholder={{
            node: (
              <Empty
                icon={<Activity className="size-8" />}
                title="No signals yet"
                description="No issues were detected in the past 7 days."
              />
            ),
          }}
          render={(issue, index) => (
            <HomeTopSignalRow
              key={issue.name}
              issue={issue}
              rank={index + 1}
              datePreset={DATE_PRESET}
              onClick={onRowClick}
            />
          )}
        />
      </VFlex>
    </ErrorBoundary>
  );
};

export default HomeTopSignals;
